<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-chalkboard-teacher" class="q-mb-sm" /> {{ $t(title_live_classes) }}
		</div>

		<filters
			class="filter-card"
			:options="getOptionsFilter"
			@requestFiltered="requestFiltered"
			@clearAndRequest="clearAndRequest"
		/>

		<q-table
			:data="live_classes"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					:debounce="600"
					icon="far fa-search"
					:placeholder="$t('search')"
				/>

				<div class="col" />

				<!-- <q-btn-dropdown
					color="secondary"
					:label="$t('reports_bi')"
					class="q-mr-sm"
					ref="dropdownReport"
					v-if="report_list.length > 0 && permissions.reports"
				>
					<q-list highlight separator link>
						<q-item
							v-for="(report, index) in report_list"
							:key="`i${index}`"
							@click.native="showReportBI(report)"
						>
							<q-item-main>
								{{ $t(report.slug) }}
							</q-item-main>
						</q-item>
					</q-list>
				</q-btn-dropdown> -->

				<q-btn
					color="secondary"
					v-if="permissions.reports"
					:disable="$buttonDelay('live_classes_report_button_delay')"
					class="q-mr-sm"
					icon="fas fa-download"
					:label="$t('live_classes_report')"
					@click="downloadReport()"
				>
					<q-tooltip
						v-if="$buttonDelay('live_classes_report_button_delay')"
						anchor="top middle"
						self="bottom middle"
						:offset="[10, 10]"
					>
						{{ $t('report_button_delay') }}
					</q-tooltip>
				</q-btn>

				<q-btn
					color="secondary"
					icon="far fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'admin_live_class', params: { id: 0 } })"
					:disable="!permissions.live_classes_manage"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props" v-show="col.show">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props" v-show="showColumn(props.colsMap, 'id')">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props" v-show="showColumn(props.colsMap, 'title')">
					<span v-if="props.row.title.length > 49">
						{{ `${props.row.title.substring(0, 50)}...` }}
						<q-tooltip>{{ props.row.title }}</q-tooltip>
					</span>
					<span v-else>
						{{ props.row.title }}
					</span>
				</q-td>

				<q-td key="code" :props="props" v-show="showColumn(props.colsMap, 'code')">
					{{ props.row.code }}
				</q-td>

				<q-td
					key="permission"
					:props="props"
					v-show="showColumn(props.colsMap, 'permission')"
				>
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantity(props.row.permission)"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
								{{ $t('areas') }}:
								<span
									v-for="(area, index) in props.row.permission.areas"
									:key="index"
								>
									{{ getArea(area) }}
									<span v-if="props.row.permission.areas.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.clients.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('clients') }}:
								<span
									v-for="(client, index) in props.row.permission.clients"
									:key="index"
								>
									{{ getClient(client) }}
									<span v-if="props.row.permission.clients.length - 1 > index"
										>,
									</span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="tags" :props="props" v-show="showColumn(props.colsMap, 'tags')">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantityTags(props.row.tags)"
						no-ripple
					>
						<q-tooltip v-if="props.row.tags.length > 0">
							<p class="tooltip q-mb-sm">
								{{ $t('tags') }}:
								<span v-for="(tag, index) in props.row.tags" :key="index">
									{{ getTag(tag) }}
									<span v-if="props.row.tags.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="owners" :props="props" v-show="showColumn(props.colsMap, 'owners')">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantityOwners(props.row.owners)"
						no-ripple
					>
						<q-tooltip v-if="props.row.owners.length > 0">
							<p class="tooltip q-mb-sm">
								{{ $t('owners') }}:
								<span v-for="(owner, index) in props.row.owners" :key="index">
									{{ owner }}
									<span v-if="props.row.owners.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td
					key="is_public"
					:props="props"
					v-show="showColumn(props.colsMap, 'is_public')"
				>
					{{ getLiveClassType(props.row.is_public) }}
				</q-td>

				<q-td
					key="platform_type"
					:props="props"
					v-show="showColumn(props.colsMap, 'platform_type')"
				>
					{{ getLiveClassPlatformTypes(props.row.platform_type) }}
				</q-td>

				<q-td
					key="live_class_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'live_class_date')"
				>
					{{ props.row.live_class_date }}
				</q-td>

				<q-td
					key="created_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'created_date')"
				>
					{{ props.row.created_date }}
				</q-td>

				<q-td
					key="updated_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'updated_date')"
				>
					{{ props.row.updated_date }}
				</q-td>

				<q-td
					key="unpublish_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'unpublish_date')"
				>
					{{ props.row.unpublish_date }}
				</q-td>

				<q-td
					key="status"
					:props="props"
					auto-width
					v-show="showColumn(props.colsMap, 'status')"
				>
					<q-chip
						small
						:icon="getIconStatus(props.row)"
						:color="getColorStatus(props.row)"
						class="full-width"
					>
						{{ $t(getStatus(props.row)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props" v-show="showColumn(props.colsMap, 'actions')">
					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_live_class',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="secondary"
						icon="far fa-chart-line"
						@click="showTrainingReport(props.row)"
					>
						<q-tooltip>
							{{ $t('view_report_for_this_live_class_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
						:disable="!permissions.live_classes_manage"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>

			<q-tr slot="bottom-row" slot-scope="props" :props="props">
				<q-td colspan="100%">
					<q-btn
						flat
						round
						dense
						color="secondary"
						size="small"
						icon="far fa-cog"
						@click="show_customize_columns = true"
					>
						<q-tooltip>
							{{ $t('configure_table_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-modal
			v-model="show_customize_columns"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="saveColumns()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('personalize_columns') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-md">
						<div
							class="col-xs-12 col-sm-4"
							v-for="(column, index) in columns"
							:key="index"
							v-if="column.label && column.label != '#'"
						>
							<q-checkbox v-model="column.show" :label="$t(column.label)" />
						</div>
					</div>

					<div class="row reverse gutter-md q-mt-md">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								icon="fal fa-times"
								color="primary"
								class="fit"
								:label="$t('close')"
								@click="saveColumns"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>

		<q-dialog v-model="show_report_problem" prevent-close>
			<span slot="title">{{ $t('report_download_failed') }}</span>

			<div slot="message">
				<span class="q-pa-xs">{{ $t('report_download_failed_exceeded_time') }}</span>
				<p class="q-pa-xs">{{ $t('the_report_it_will_be_sent_to_your_email') }}</p>
			</div>

			<template slot="buttons" slot-scope="props" :props="props" class="justify-center">
				<div class="row full-width q-mb-xs">
					<q-btn
						color="positive"
						class="offset-4 col-4"
						:label="$t('ok')"
						@click="show_report_problem = false"
					/>
				</div>
			</template>
		</q-dialog>

		<reports
			v-if="show_report_training"
			:show_report_training="show_report_training"
			:permissions="permissions.reports"
			:id="action_id"
			:title="action_title"
			@hide_report="hideReport()"
		/>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import Filters from 'components/admin/live_classes/Filters.vue'

export default {
	i18nOptions: {
		namespaces: [
			'custom',
			'common',
			'admin',
			'imports',
			'live_classes',
			'courses',
			'presential',
		],
	},

	components: {
		reports: () => import('components/admin/live_classes/Report.vue'),
		filters: Filters,
	},

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			live_classes: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
					show: true,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: true,
					show: true,
				},
				{
					name: 'code',
					required: true,
					label: 'code',
					align: 'left',
					field: 'code',
					sortable: true,
					show: false,
				},
				{
					name: 'permission',
					required: true,
					label: 'permission',
					align: 'left',
					field: 'permission',
					sortable: false,
					show: false,
				},
				{
					name: 'tags',
					required: true,
					label: 'tags',
					align: 'left',
					field: 'tags',
					sortable: false,
					show: true,
				},
				{
					name: 'owners',
					required: true,
					label: 'owners',
					align: 'left',
					field: 'owners',
					sortable: false,
					show: true,
				},
				{
					name: 'is_public',
					required: true,
					label: 'live_class_type',
					align: 'left',
					field: 'is_public',
					sortable: false,
					show: true,
				},
				{
					name: 'platform_type',
					required: true,
					label: 'platform',
					align: 'left',
					field: 'platform_type',
					sortable: false,
					show: true,
				},
				{
					name: 'live_class_date',
					required: true,
					label: 'live_class_date',
					align: 'left',
					field: 'live_class_date',
					sortable: true,
					show: true,
				},
				{
					name: 'created_date',
					required: true,
					label: 'created_date',
					align: 'left',
					field: 'created_date',
					sortable: true,
					show: true,
				},
				{
					name: 'updated_date',
					required: true,
					label: 'updated_date',
					align: 'left',
					field: 'updated_date',
					sortable: true,
					show: true,
				},
				{
					name: 'unpublish_date',
					required: true,
					label: 'unpublish_date',
					align: 'left',
					field: 'unpublish_date',
					sortable: true,
					show: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
					show: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
					show: true,
				},
			],
			filter: '',
			filter_area: [],
			filter_profile: [],
			filter_status: [],
			filter_tag: [],
			filter_start_date: [],
			filter_end_date: [],
			filter_transmission_start_date: [],
			filter_transmission_end_date: [],
			action_id: '',
			confirm_remove: false,
			action_id: '',
			action_status: '',
			action_title: '',
			show_report_training: false,
			show_report_problem: false,
			show_customize_columns: false,
			permissions: {
				live_classes_manage: false,
				reports: false,
			},
			show_report: false,
			report_list: [],
			report_url: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			reload_content: 'common/reload_content',
			user_clients: 'common/user_clients',
			areas: 'registration_lists/country_areas',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			reports: 'registration_lists/reports',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			user_client_module_items: 'common/user_client_module_items',
			num_rows: 'datatable/num_rows',
			live_classes_columns: 'datatable_columns/live_classes_columns',
			live_class_platform_types: 'registration_lists/live_class_platform_types',
			reports_store: 'registration_lists/reports',
		}),

		getOptionsFilter() {
			return {
				areas: this.areas ? this.areas : [],
				profiles: this.profiles ? this.profiles : [],
				statuses: [
					{ value: 1, label: this.$t('published') },
					{ value: 2, label: this.$t('unpublished') },
					{ value: 3, label: this.$t('deleted') },
					{ value: 4, label: this.$t('completed') },
				],
				tags: this.tags ? this.tags : [],
			}
		},

		title_live_classes() {
			if (this.id_user_client_selected) {
				const obj = this.clients.find((obj) => obj.id === this.id_user_client_selected)
				if (!obj.title_live_classes) {
					return 'live_classes'
				}
				if (obj) {
					return obj.title_live_classes === '' ? 'live_classes' : obj.title_live_classes
				}
			}
			return 'live_classes'
		},

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getMaxWidth() {
			return this.$q.platform.is.mobile ? '100vw' : '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			const order = pagination.descending ? '-' : ''
			const order_by = pagination.sortBy ? order + pagination.sortBy : ''

			const data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				areas__id__in: this.filter_area.join(','),
				profiles__id__in: this.filter_profile.join(','),
				tags__id__in: this.filter_tag.join(','),
				status__id: this.filter_status.join(','),
				created_date__gte: this.filter_start_date,
				created_date__lte: this.filter_end_date,
				date_class__gte: this.filter_transmission_start_date,
				date_class__lte: this.filter_transmission_end_date,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/admin_live_classes/live_classes/?${this.$qs.stringify(data, {
						arrayFormat: 'repeat',
					})}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.live_classes = []

					this.live_classes = response.data.results.map((r) => {
						return {
							...r,
							live_class_date: date.formatDate(r.date_class, 'DD/MM/YYYY HH:mm'),
							created_date: date.formatDate(r.created_date, 'DD/MM/YYYY HH:mm'),
							updated_date: date.formatDate(r.updated_date, 'DD/MM/YYYY HH:mm'),
							code: r.code,
							actions: r.id,
							title: r.title,
							description: r.description,
							is_public: r.is_public,
							platform_type: r.platform_type,
							status: r.status,
							tags: r.tags,
							owners: r.list_owners.map((o) => o.full_name),
							permission: {
								areas: r.areas,
								clients: r.clients,
							},
							unpublish_date: r.unpublish_date
								? moment(r.unpublish_date)
										.tz('Etc/GMT')
										.format('DD/MM/YYYY')
								: '',
						}
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		clearAndRequest() {
			;(this.filter_area = []),
				(this.filter_profile = []),
				(this.filter_status = []),
				(this.filter_tag = []),
				(this.filter_start_date = []),
				(this.filter_end_date = []),
				(this.filter_transmission_start_date = []),
				(this.filter_transmission_end_date = []),
				this.request({
					pagination: {
						page: 1,
						rowsNumber: 10,
						rowsPerPage: this.num_rows,
						descending: false,
						sortBy: null,
					},
				})
		},

		requestFiltered({
			area,
			profile,
			status,
			tag,
			start_date,
			end_date,
			transmission_start_date,
			transmission_end_date,
		}) {
			;(this.filter_area = area), (this.filter_profile = profile)
			this.filter_status = status
			this.filter_tag = tag
			this.filter_start_date = start_date
			this.filter_end_date = end_date
			this.filter_transmission_start_date = transmission_start_date
			this.filter_transmission_end_date = transmission_end_date

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		showColumn(list, name) {
			if (list[name]) return list[name].show
			else return false
		},

		saveColumns() {
			this.show_customize_columns = false
			this.saveDatatableColumns()
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_live_classes/live_classes/${this.action_id}/`, this.getHeader)
				.then(() => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		downloadReport() {
			if (this.$buttonDelay('live_classes_report_button_delay')) return
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})
			this.$axios
				.get(`admin_live_classes/live_classes/generate_report/`, this.getHeader)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$store.dispatch('common/SET_LIVE_CLASSES_REPORT_BUTTON_DELAY', [
						new Date(),
					])
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		showTrainingReport(training) {
			this.action_id = training.id
			this.action_status = training.status
			this.action_title = training.title
			this.show_report_training = true
		},

		hideReport() {
			this.action_id = ''
			this.show_report_training = false
			this.loadPagination()
		},

		getStatus(row) {
			if (row.status == 1) {
				if (!row.is_open) return 'published'
				else return 'open'
			} else if (row.status == 2) return 'unpublished'
			else if (row.status == 3) return 'deleted'
			else if (row.status == 4) return 'finished'
		},

		getIconStatus(row) {
			if (row.status == 1) {
				if (!row.is_open) return 'fas fa-check'
				else return 'fas fa-clock'
			} else if (row.status == 2) return 'fas fa-ban'
			else if (row.status == 3) return 'fas fa-trash-alt'
			else if (row.status == 4) return 'fas fa-check'
		},

		getColorStatus(row) {
			if (row.status == 1) {
				if (!row.is_open) return 'positive'
				else return 'info'
			} else if (row.status == 2) return 'warning'
			else if (row.status == 3) return 'negative'
			else if (row.status == 4) return 'info'
			else if (row.status == 5) return 'info'
		},

		getLiveClassType(is_public) {
			return is_public ? this.$t('subscription') : this.$t('invite')
		},

		getLiveClassPlatformTypes(platform) {
			if (this.live_class_platform_types) {
				const platformType = this.live_class_platform_types.find(
					(type) => platform === type.slug
				)
				return platformType && platformType.label ? platformType.label : ''
			} else return ''
		},

		getKnowledgeArea(id) {
			let area = this.areas.find((obj) => obj.value === id)
			return area ? area.label : ''
		},

		getQuantity(permission) {
			return permission.areas.length + permission.clients.length
		},

		getArea(area) {
			const obj = this.areas.find((obj) => obj.value === area)
			return obj ? obj.label : ''
		},

		getClient(client) {
			if (this.user_clients) {
				const obj = this.user_clients.find((obj) => obj.id === client)
				if (obj) return obj.name
			}
			return ''
		},

		getUserGroup(user_group) {
			const obj = this.user_groups.find((obj) => obj.value === user_group)
			return obj ? obj.label : ''
		},

		getUserProfile(profile) {
			const obj = this.profiles.find((obj) => obj.value === profile)
			return obj ? obj.label : ''
		},

		getQuantityTags(tags) {
			return tags.length > 0 ? tags.length : '0'
		},

		getQuantityOwners(owners) {
			return owners.length > 0 ? owners.length : '0'
		},

		getTag(tag) {
			const obj = this.tags.find((obj) => obj.value === tag)
			return obj ? obj.label : ''
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		saveDatatableColumns() {
			this.$store.dispatch('datatable_columns/SET_LIVE_CLASSES_COLUMNS', [this.columns])
		},

		loadDatatableColumns() {
			if (process.env.PROJECT_FOLDER !== 'universidadechevrolet') {
				this.columns = this.columns.filter((c) => c.name !== 'code')

				const storedCol = this.live_classes_columns.find((c) => c.name === 'code')
				if (storedCol) {
					this.$store.dispatch('datatable_columns/SET_LIVE_CLASSES_COLUMNS', [
						this.columns,
					])
				}
			}
			if (this.$compareStoredColumns(this.columns, this.live_classes_columns))
				this.columns = this.live_classes_columns
			else return
		},

		checkPermissions() {
			const client = this.user_client_module_items.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (client) {
				for (const [key, value] of Object.entries(this.permissions)) {
					let obj = client.module_items.find((obj) => obj.slug === key)
					if (obj) {
						this.permissions[key] = obj.permission
					}
				}
			} else return
		},

		loadReports() {
			if (this.reports_store)
				this.report_list = this.reports_store.filter((obj) => {
					return (
						obj.slug_type === 'live_classes' &&
						obj.url != 'https://www.take5.com.br' &&
						obj.url != ''
					)
				})
		},

		getAuthParamsData(url_original) {
			this.$q.loading.show()
			this.$axios
				.get('/reports/reports/get_looker_studio_challenge_token/', this.getHeader)
				.then((response) => {
					this.report_url = `${url_original}?${response.data.params}`
					this.show_report = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		showReportBI(report) {
			this.$refs.dropdownReport.hide()
			if (report.report_type === 3) {
				this.getAuthParamsData(report.url_original)
			} else {
				this.report_url = report.url
				this.show_report = true
			}
		},
	},

	watch: {
		reload_content: function() {
			this.request({
				pagination: this.server_pagination,
			})
		},
	},

	created() {
		this.loadReports()
		this.loadDatatableColumns()
		this.loadPagination()
		this.fetchLists()
		this.checkPermissions()
	},
}
</script>

<style lang="stylus" scoped>
.filter-card
    border-radius 4px
    box-shadow 0 1px 2px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12)
</style>
